import type { QueryClient } from '@tanstack/react-query';
import { DEFAULT_MODULE_STATE, staffStageLabels, type AppSettingsView, type ApplicationStage, type ModuleKey, type ModuleState } from '@nest-recruit/shared';
import { APP_SETTINGS_KEY, useAppSettings } from './app-settings';

// R1 (§L28): which switchable modules are on, for every <ModuleGate>, the module bar and the
// palette. Read from the app settings loaded at sign-in; an HR Lead's change reaches every open
// browser as an invalidation hint, and TanStack Query refetches — no reload.

/** The §L34 defaults until the settings have loaded (they are prefetched before the shell paints). */
export function useModules(): ModuleState {
  return useAppSettings().data?.modules ?? DEFAULT_MODULE_STATE;
}

export function useModule(key: ModuleKey): boolean {
  return useModules()[key];
}

/** §L33: the stage names staff see — `offer` reads "Selected" while Offers is off (R8). */
export function useStageLabels(): Readonly<Record<ApplicationStage, string>> {
  return staffStageLabels(useModule('offers'));
}

/** The same, outside React — a palette command deciding where to go when it runs. */
export function modulesIn(queryClient: QueryClient): ModuleState {
  return queryClient.getQueryData<AppSettingsView>(APP_SETTINGS_KEY)?.modules ?? DEFAULT_MODULE_STATE;
}

/** What the global search covers, worded from the modules that are on (no "pools" while Talent pools is off). */
export function searchScopeLabel(modules: ModuleState): string {
  const parts = ['Candidates', 'jobs'];
  if (modules.requisitions) parts.push('requisitions');
  if (modules.pools) parts.push('pools');
  const last = parts.pop();
  return `${parts.join(', ')} and ${last}`;
}
